/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { CompanyDTO, CompanyFilterDTO, ProductDTO, ProductFilterDTO, UserDTO, UserFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IProductMaintenanceProps {
    data: any;
    parentId: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const ProductMaintenance: React.FC<IProductMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted, parentId }) => {

    const [objectiveList, setObjectiveList] = useState<ValueListDTO[]>([]);
    const [lineList, setLineList] = useState<ValueListDTO[]>([]);
    const [screenAniloxNumberList, setScreenAniloxNumber] = useState<ValueListDTO[]>([]);
    const [dryingList, setDryingList] = useState<ValueListDTO[]>([]);
    const [printTypeList, setPrintTypeList] = useState<ValueListDTO[]>([]);
    const [supportList, setSupportList] = useState<ValueListDTO[]>([]);
    const [reactionList, setReactionList] = useState<ValueListDTO[]>([]);
    const [enduranceList, setEnduranceList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [productDTO, setProductDTO] = useState<ProductDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(()=> {
        setProductDTO({
            ...productDTO,
            id: '',
            name: '',
            code: '',
            description: '',
            developmentReference: '',
            printTypeId: '',
            objectiveId: '',
            quantity: '',
            supportId: '',
            lineId: '',
            dryingId: '',
            screenAniloxNumberId: '',
            enduranceId: '',
            reactionId: '',
            technicalIndication: '',
            openingId: parentId,
            companyId: userInfo.user.companyId
        } as ProductDTO);
    }, [data]);

    useEffect(() => {
        
        if(isOpenMaintenance){
            handleListGetAll(ListId.corpsphere.product.objectiveId, setObjectiveList);
            handleListGetAll(ListId.corpsphere.product.lineId, setLineList);
            handleListGetAll(ListId.corpsphere.product.screenAniloxNumberId, setScreenAniloxNumber);
            handleListGetAll(ListId.corpsphere.product.penetrationId, setDryingList);
            handleListGetAll(ListId.corpsphere.product.printTypeId, setPrintTypeList);
            handleListGetAll(ListId.corpsphere.product.supportId, setSupportList);
            handleListGetAll(ListId.corpsphere.product.reactionId, setReactionList);
            handleListGetAll(ListId.corpsphere.product.enduranceId, setEnduranceList);
            handleCompanyGetAll();
            handleUserGetAllAsync(setUserList);
            handleGetProductByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetProductByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('corpsphere.product', data.id, setLoading, setProductDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = productDTO as ProductDTO;

        if (productDTO?.id === '' || productDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('corpsphere.product', dto, setProductDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('corpsphere.product', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [productDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='SDT - PRODUCT DETAILS'
                icon='grid_view_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={productDTO?.name}
                                setValue={setProductDTO}
                                obj={productDTO}
                                required={true}
                                maxLength={20}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='code'
                                label='Code'
                                value={productDTO?.code}
                                setValue={setProductDTO}
                                obj={productDTO}
                                required={true}
                                maxLength={20}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='objectiveId'
                                label='Objective'
                                placeholder='Objective'
                                value={productDTO?.objectiveId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={objectiveList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='lineId'
                                label='Line'
                                placeholder='Line'
                                value={productDTO?.lineId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={lineList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='quantity'
                                label='Quantity'
                                value={productDTO?.quantity}
                                setValue={setProductDTO}
                                obj={productDTO}
                                required={true}
                                maxLength={20}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='screenAniloxNumberId'
                                label='Screen / Anilox N'
                                placeholder='Drying'
                                value={productDTO?.screenAniloxNumberId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={screenAniloxNumberList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='dryingId'
                                label='Drying'
                                placeholder='Drying'
                                value={productDTO?.dryingId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={dryingList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='printTypeId'
                                label='Print Type'
                                placeholder='Print Type'
                                value={productDTO?.printTypeId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={printTypeList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='supportId'
                                label='Support'
                                placeholder='Support'
                                value={productDTO?.supportId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={supportList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='reactionId'
                                label='Reaction'
                                placeholder='Reaction'
                                value={productDTO?.reactionId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={reactionList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='enduranceId'
                                label='Endurance'
                                placeholder='Endurance'
                                value={productDTO?.enduranceId}
                                setValue={setProductDTO}
                                obj={productDTO}
                                valueList={enduranceList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={productDTO?.description}
                                setValue={setProductDTO}
                                obj={productDTO}
                                maxLength={4000}
                                multiline={true}
                                rows={4}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='developmentReference'
                                label='Development Reference'
                                value={productDTO?.developmentReference}
                                setValue={setProductDTO}
                                obj={productDTO}
                                maxLength={4000}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {productDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={productDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === productDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(productDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === productDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(productDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};